/**
 * minimapProjection — World ↔ minimap pixel math + marker gather (AR-06).
 * Domain-only; no DOM / canvas.
 *
 * Iso angle matches Murder Bridge under camera (+X/+Z): lane BL→TR on screen.
 */

const STRUCTURE_TYPES = new Set(['turret', 'inhibitor', 'nexus']);

/** Degrees: sandbox ARAM lane under isometric camera (aramBridgeMeshes). */
export const MINIMAP_ISO_DEG = 45;

function clamp01(n) {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function rotateXZ(x, z, rad) {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return {
    u: x * c - z * s,
    v: x * s + z * c
  };
}

function boundsUvExtents(bounds, rad) {
  const corners = [
    [bounds.minX, bounds.minZ],
    [bounds.minX, bounds.maxZ],
    [bounds.maxX, bounds.minZ],
    [bounds.maxX, bounds.maxZ]
  ];
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  for (const [x, z] of corners) {
    const { u, v } = rotateXZ(x, z, rad);
    if (u < minU) minU = u;
    if (u > maxU) maxU = u;
    if (v < minV) minV = v;
    if (v > maxV) maxV = v;
  }
  return { minU, maxU, minV, maxV };
}

/**
 * Project sandbox (x,z) into minimap pixels.
 * angleDeg 0: axis-aligned (+Z toward top).
 * angleDeg 45: iso BL→TR (blue +Z bottom-left, red −Z top-right).
 */
export function worldToMinimapPx(x, z, bounds, width, height, options = {}) {
  const angleDeg = options.angleDeg ?? 0;
  if (!angleDeg) {
    const spanX = bounds.maxX - bounds.minX || 1;
    const spanZ = bounds.maxZ - bounds.minZ || 1;
    const nx = clamp01((x - bounds.minX) / spanX);
    const nz = clamp01((z - bounds.minZ) / spanZ);
    return {
      px: nx * width,
      py: (1 - nz) * height
    };
  }

  const rad = (angleDeg * Math.PI) / 180;
  const { u, v } = rotateXZ(x, z, rad);
  const ext = boundsUvExtents(bounds, rad);
  const spanU = ext.maxU - ext.minU || 1;
  const spanV = ext.maxV - ext.minV || 1;
  const nu = clamp01((u - ext.minU) / spanU);
  const nv = clamp01((v - ext.minV) / spanV);
  // Larger v (+Z / blue) → bottom of frame (matches isometric BL→TR).
  return {
    px: nu * width,
    py: nv * height
  };
}

/**
 * Inverse of worldToMinimapPx (SPY-414). Click coords → world x/z.
 */
export function minimapPxToWorld(px, py, bounds, width, height, options = {}) {
  const angleDeg = options.angleDeg ?? 0;
  const w = width || 1;
  const h = height || 1;
  if (!angleDeg) {
    const spanX = bounds.maxX - bounds.minX || 1;
    const spanZ = bounds.maxZ - bounds.minZ || 1;
    const nx = clamp01(px / w);
    const nz = clamp01(1 - py / h);
    return {
      x: bounds.minX + nx * spanX,
      z: bounds.minZ + nz * spanZ
    };
  }

  const rad = (angleDeg * Math.PI) / 180;
  const ext = boundsUvExtents(bounds, rad);
  const spanU = ext.maxU - ext.minU || 1;
  const spanV = ext.maxV - ext.minV || 1;
  const nu = clamp01(px / w);
  const nv = clamp01(py / h);
  const u = ext.minU + nu * spanU;
  const v = ext.minV + nv * spanV;
  const inv = rotateXZ(u, v, -rad);
  return { x: inv.u, z: inv.v };
}

function markerKind(entity) {
  if (entity.type === 'champion') return 'champion';
  if (entity.type === 'minion') return 'minion';
  if (STRUCTURE_TYPES.has(entity.type)) return 'structure';
  return 'other';
}

function isLiving(entity) {
  if (!entity) return false;
  if (entity.isDead === true) return false;
  if (typeof entity.hp === 'number' && entity.hp <= 0) return false;
  return true;
}

/**
 * Flatten living entities + active relics into minimap markers.
 */
export function buildMinimapMarkers({ entities = [], relics = [] } = {}) {
  const markers = [];
  for (const entity of entities) {
    if (!isLiving(entity)) continue;
    markers.push({
      id: entity.id,
      team: entity.team || 'neutral',
      kind: markerKind(entity),
      x: entity.x,
      z: entity.z
    });
  }
  for (const relic of relics) {
    if (!relic || relic.active === false) continue;
    markers.push({
      id: relic.id,
      team: 'neutral',
      kind: 'relic',
      x: relic.x,
      z: relic.z
    });
  }
  return markers;
}

/**
 * Diagonal (or vertical) lane stroke for bridge minimaps.
 * Endpoints: mid-X at maxZ (blue) → mid-X at minZ (red).
 */
export function bridgeSilhouetteBand(bounds, width, height, options = {}) {
  const midX = (bounds.minX + bounds.maxX) / 2;
  const blue = worldToMinimapPx(midX, bounds.maxZ, bounds, width, height, options);
  const red = worldToMinimapPx(midX, bounds.minZ, bounds, width, height, options);
  const spanX = Math.abs(bounds.maxX - bounds.minX) || 1;
  const spanZ = Math.abs(bounds.maxZ - bounds.minZ) || 1;
  const thicknessRatio = Math.min(0.28, Math.max(0.12, spanX / spanZ));
  const lineWidth = Math.max(8, Math.min(width, height) * thicknessRatio);
  return {
    x1: blue.px,
    y1: blue.py,
    x2: red.px,
    y2: red.py,
    lineWidth
  };
}

/** @deprecated Prefer bridgeSilhouetteBand (iso-aware). */
export function bridgeSilhouetteRect(bounds, width, height) {
  const band = bridgeSilhouetteBand(bounds, width, height, { angleDeg: 0 });
  const laneW = band.lineWidth;
  return {
    x: (width - laneW) / 2,
    y: Math.min(band.y1, band.y2),
    w: laneW,
    h: Math.abs(band.y1 - band.y2)
  };
}
